import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { Icon } from "@iconify/react";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { usePagination } from "app/hooks/usePagination";
import { Table as CustomTable } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  importMeeshoOrders,
  getMeeshoImportHistory,
  getMeeshoImportHistoryDetail,
} from "app/redux/slice/meeshoSlice";
import Loader from "@jumbo/components/Loader";
import { useNavigate } from "react-router-dom";
import { MEESHO } from "app/routes/constant";

const MeeshoBulkImport = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const meeshoData = useSelector((state) => state?.meesho);
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImport, setSelectedImport] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [detailLoading, setDetailLoading] = useState(false);

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "import-history" });

  // Get import history data from Redux store
  const importHistory = meeshoData?.importHistory?.list || [];

  // Load import history data
  const getImportHistoryData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getMeeshoImportHistory(value));
  };

  useEffect(() => {
    getImportHistoryData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "Processing":
        return "warning";
      case "Failed":
        return "error";
      default:
        return "default";
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };
  const handleViewDetails = async (row) => {
    setDetailLoading(true);
    try {
      const response = await dispatch(
        getMeeshoImportHistoryDetail(row._id)
      ).unwrap();
      if (response.status === 200) {
        setSelectedImport(response.data?.data?.importHistory || row);
        setOpenDialog(true);
      } else {
        enqueueSnackbar("Failed to fetch import details", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch import details", {
        variant: "error",
      });
    } finally {
      setDetailLoading(false);
    }
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedImport(null);
  };

  const columns = [
    {
      id: "originalFileName",
      label: "File Name",
      sort: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {row.originalFileName || row.fileName || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(row.fileSize)}
          </Typography>
        </Box>
      ),
    },
    {
      id: "status",
      label: "Status",
      sort: true,
      render: (row) => (
        <Chip
          label={row.status || "Unknown"}
          color={getStatusColor(row.status)}
          size="small"
        />
      ),
    },
    {
      id: "totalRecords",
      label: "Records Summary",
      sort: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Total: {row.totalRecords || 0}
          </Typography>
          <Typography variant="caption" color="success.main">
            Success: {row.successfulImports || 0}
          </Typography>
          <Typography variant="caption" color="error.main" sx={{ ml: 1 }}>
            Failed: {row.failedImports || 0}
          </Typography>
          {row.duplicateRecords > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
              Duplicates: {row.duplicateRecords}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: "summary",
      label: "Order Summary",
      sort: false,
      render: (row) => (
        <Box>
          {row.summary && (
            <>
              <Typography variant="caption" color="primary.main">
                New: {row.summary.newOrders || 0}
              </Typography>
              <Typography variant="caption" color="info.main" sx={{ ml: 1 }}>
                Updated: {row.summary.updatedOrders || 0}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                Skipped: {row.summary.skippedOrders || 0}
              </Typography>
              {row.summary.totalAmount > 0 && (
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ display: "block" }}
                >
                  Amount: {formatCurrency(row.summary.totalAmount)}
                </Typography>
              )}
            </>
          )}
        </Box>
      ),
    },
    {
      id: "importedBy",
      label: "Imported By",
      sort: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {row.importedBy?.name || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.importedBy?.email || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "createdAt",
      label: "Import Date",
      sort: true,
      render: (row) => formatDate(row.createdAt),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewDetails(row)}
            disabled={detailLoading}
          >
            View
          </Button>
        </Box>
      ),
    },
  ];

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getImportHistoryData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  const handleFileSelect = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      handleImportFile(file, type);
    }
  };

  const handleImportFile = async (file, type) => {
    if (!file) {
      enqueueSnackbar("Please select a file to upload", { variant: "error" });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    try {
      const response = await dispatch(importMeeshoOrders(formData)).unwrap();
      if (response.status === 200) {
        enqueueSnackbar(response.data?.responseMessage || "Import successful", {
          variant: "success",
        });
        // Refresh import history
        getImportHistoryData();
      } else {
        enqueueSnackbar(response.data?.responseMessage || "Import failed", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Import failed. Please try again.", {
        variant: "error",
      });
    }
  };

  const handleImportOrders = () => {
    document.getElementById("orders-file-input").click();
  };

  return (
    <Box>
      {/* Hidden file inputs */}
      <input
        id="orders-file-input"
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: "none" }}
        onChange={(e) => {
          handleFileSelect(e, "orders");
          e.target.value = "";
        }}
      />

      {meeshoData?.importLoading && <Loader />}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: "bold" }}>
          Orders Import Management
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{ width: "100px" }}
          startIcon={<Icon icon="ep:back" width="24" height="24" />}
          onClick={() => navigate(MEESHO)}
        >
          Back
        </Button>
      </div>

      {/* Import Options */}
      <Grid container spacing={3} sx={{ mb: 3, justifyContent: "center" }}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              },
              border: "2px solid #2196f320",
              borderRadius: 2,
            }}
            onClick={handleImportOrders}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Icon
                icon="material-symbols:shopping-cart"
                style={{
                  fontSize: "48px",
                  color: "#2196f3",
                  marginBottom: "16px",
                }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", mb: 1, color: "#2196f3" }}
              >
                Import Orders
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload and process Meesho order data
              </Typography>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#2196f3" }}
                startIcon={<Icon icon="material-symbols:upload" />}
              >
                Choose File
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box>
            {/* Search */}
            <Card sx={{ display: "flex", mb: 3 }}>
              <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
                <CardContent>
                  <Grid container spacing={{ xs: 2, md: 3 }}>
                    <Grid item xs={12} sm={10}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search Import History"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              style={{
                                display: searchText === "" ? "none" : "flex",
                              }}
                              onClick={() => setSearchText("")}
                            >
                              <ClearIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        onClick={() => {
                          handleChangePage(0, 0);
                          getImportHistoryData();
                        }}
                      >
                        Search
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Div>
            </Card>

            {/* Import History Table */}
            <Card>
              <CardContent>
                <Stack
                  justifyContent="space-between"
                  direction={{ xs: "column", sm: "row" }}
                  sx={{ mb: 3 }}
                ></Stack>
                <CustomTable
                  columns={columns}
                  rows={importHistory}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  count={meeshoData?.importHistory?.total || 0}
                  Order={order}
                  OrderBy={orderBy}
                  sortData={sortData}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  loading={meeshoData?.importHistory?.loading}
                />
              </CardContent>
            </Card>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Icon
              icon="material-symbols:description"
              style={{ fontSize: "24px", color: "#2874f0" }}
            />
            <Typography variant="h6">
              Import Details -{" "}
              {selectedImport?.originalFileName || selectedImport?.fileName}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImport && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Basic Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: "#2874f0" }}>
                      Basic Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Status:
                        </Typography>
                        <Chip
                          label={selectedImport.status}
                          color={getStatusColor(selectedImport.status)}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Original File Name:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {selectedImport.originalFileName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          System File Name:
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {selectedImport.fileName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          File Size:
                        </Typography>
                        <Typography variant="body1">
                          {formatFileSize(selectedImport.fileSize)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Processing Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: "#2874f0" }}>
                      Processing Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Total Records:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {selectedImport.totalRecords || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Successful:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "success.main", fontWeight: "bold" }}
                        >
                          {selectedImport.successfulImports || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Failed:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "error.main", fontWeight: "bold" }}
                        >
                          {selectedImport.failedImports || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Duplicates:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "warning.main", fontWeight: "bold" }}
                        >
                          {selectedImport.duplicateRecords || 0}
                        </Typography>
                      </Grid>

                      {/* Processing Metrics */}
                      {(selectedImport.processingTime ||
                        selectedImport.processingTimeFormatted) && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Processing Time:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold" }}
                          >
                            {selectedImport.processingTimeFormatted ||
                              `${selectedImport.processingTime}ms`}
                          </Typography>
                        </Grid>
                      )}

                      {/* Success/Error Rates */}
                      <Grid item xs={12}>
                        <Grid container spacing={1}>
                          {selectedImport.successRate && (
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Success Rate:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "success.main",
                                  fontWeight: "bold",
                                }}
                              >
                                {selectedImport.successRate}%
                              </Typography>
                            </Grid>
                          )}
                          {selectedImport.errorRate && (
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Error Rate:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "error.main",
                                  fontWeight: "bold",
                                }}
                              >
                                {selectedImport.errorRate}%
                              </Typography>
                            </Grid>
                          )}
                          {selectedImport.duplicateRate && (
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Duplicate Rate:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "warning.main",
                                  fontWeight: "bold",
                                }}
                              >
                                {selectedImport.duplicateRate}%
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Timeline Information */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: "#2874f0" }}>
                      <Icon
                        icon="material-symbols:schedule"
                        style={{ marginRight: "8px", fontSize: "20px" }}
                      />
                      Timeline
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Start Time:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedImport.startTime)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          End Time:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedImport.endTime)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Created At:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedImport.createdAt)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Last Updated:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedImport.updatedAt)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Imported By:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <Icon
                            icon="material-symbols:person"
                            style={{ fontSize: "16px", color: "#666" }}
                          />
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: "bold" }}
                            >
                              {selectedImport.importedBy?.name || "System"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {selectedImport.importedBy?.email || ""}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Order Summary Information */}
              {selectedImport.summary && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, color: "#2874f0" }}>
                        Order Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            New Orders:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ color: "primary.main", fontWeight: "bold" }}
                          >
                            {selectedImport.summary.newOrders || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Updated Orders:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ color: "info.main", fontWeight: "bold" }}
                          >
                            {selectedImport.summary.updatedOrders || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Skipped Orders:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ color: "text.secondary", fontWeight: "bold" }}
                          >
                            {selectedImport.summary.skippedOrders || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Total Amount:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ color: "success.main", fontWeight: "bold" }}
                          >
                            {formatCurrency(selectedImport.summary.totalAmount)}
                          </Typography>
                        </Grid>

                        {/* Date Range */}
                        {selectedImport.summary.dateRange && (
                          <>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Earliest Order Date:
                              </Typography>
                              <Typography variant="body1">
                                {selectedImport.summary.dateRange.earliest
                                  ? formatDate(
                                      selectedImport.summary.dateRange.earliest
                                    )
                                  : "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Latest Order Date:
                              </Typography>
                              <Typography variant="body1">
                                {selectedImport.summary.dateRange.latest
                                  ? formatDate(
                                      selectedImport.summary.dateRange.latest
                                    )
                                  : "N/A"}
                              </Typography>
                            </Grid>
                          </>
                        )}

                        {/* Order States */}
                        {selectedImport.summary.orderStates &&
                          selectedImport.summary.orderStates.length > 0 && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                Order States:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                {selectedImport.summary.orderStates.map(
                                  (stateObj, index) => (
                                    <Chip
                                      key={index}
                                      label={`${stateObj.state} (${stateObj.count})`}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                    />
                                  )
                                )}
                              </Box>
                            </Grid>
                          )}

                        {/* Platforms */}
                        {selectedImport.summary.platforms &&
                          selectedImport.summary.platforms.length > 0 && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                Platforms:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                {selectedImport.summary.platforms.map(
                                  (platformObj, index) => (
                                    <Chip
                                      key={index}
                                      label={`${platformObj.platform} (${platformObj.count})`}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                    />
                                  )
                                )}
                              </Box>
                            </Grid>
                          )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Error Information (if any) */}
              {(selectedImport.errorMessage ||
                (selectedImport.issueDetails &&
                  selectedImport.issueDetails.length > 0)) && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderColor: "error.main" }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, color: "error.main" }}
                      >
                        <Icon
                          icon="material-symbols:error"
                          style={{ marginRight: "8px", fontSize: "20px" }}
                        />
                        Error Details (
                        {selectedImport.issueDetails?.length || 0} issueDetails)
                      </Typography>

                      {selectedImport.errorMessage && (
                        <Typography
                          variant="body2"
                          color="error.main"
                          sx={{ mb: 2 }}
                        >
                          {selectedImport.errorMessage}
                        </Typography>
                      )}

                      {selectedImport.issueDetails &&
                        selectedImport.issueDetails.length > 0 && (
                          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                            {selectedImport.issueDetails.map((error, index) => (
                              <Card
                                key={error._id || index}
                                variant="outlined"
                                sx={{
                                  mb: 2,
                                  borderColor: "error.light",
                                  // backgroundColor: "error.light",
                                  backgroundColor: "rgba(244, 67, 54, 0.04)",
                                }}
                              >
                                <CardContent sx={{ py: 2 }}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={2}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Row:
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: "bold" }}
                                      >
                                        {error.row}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Field:
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: "bold" }}
                                      >
                                        {error.field}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={7}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Error:
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        color="error.main"
                                      >
                                        {error.error}
                                      </Typography>
                                    </Grid>

                                    {error.value && (
                                      <Grid item xs={12}>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Order Id:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontFamily: "monospace",
                                            backgroundColor: "rgba(0,0,0,0.05)",
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            display: "inline-block",
                                          }}
                                        >
                                          {error.value}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {detailLoading && <Loader />}
    </Box>
  );
};

export default MeeshoBulkImport;
